import type { HTMLProps } from 'react';
import type { Control } from 'react-hook-form';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CountryCode } from '~/api/common/types';

import { CountryFlag } from '~/components';
import { Combobox, Field } from '~/components/Form';
import { useIntl } from '~/hooks/useIntl';

import { countryNameStyle, countryStyle } from './FormCountryCode.css';
import { type CountryCodeGroup, useCountryCodeGroups } from './useCountryCodeGroups';

type FormWithCountryCode = { address: { countryCode: CountryCode } };

export const FormCountryCode = <T extends FormWithCountryCode>({
  control,
  disabled,
  ...props
}: HTMLProps<HTMLDivElement> & {
  control: T extends FormWithCountryCode ? Control<T> : never;
}) => {
  const { displayNameOfCountry } = useIntl();
  const { t } = useTranslation(['clients', 'validation']);

  const countryCodeGroups = useCountryCodeGroups();

  return (
    <div {...props}>
      <Controller
        control={control as Control<FormWithCountryCode>}
        name="address.countryCode"
        render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('clients:fields.countryCode.label')}</Field.Label>

            <Combobox.Root
              disabled={disabled}
              items={countryCodeGroups}
              itemToStringLabel={displayNameOfCountry}
              onValueChange={onChange}
              value={value}
            >
              <Combobox.Button
                adornment={value && <CountryFlag countryCode={value} />}
                clearable={false}
                placeholder={t('clients:fields.countryCode.placeholder')}
              />

              <Combobox.List>
                {(group: CountryCodeGroup) => (
                  <Combobox.Group items={group.items} key={group.label}>
                    <Combobox.GroupLabel>{group.label}</Combobox.GroupLabel>

                    <Combobox.Collection>
                      {(countryCode: CountryCode) => (
                        <Combobox.Item key={countryCode} value={countryCode}>
                          <span className={countryStyle}>
                            <CountryFlag countryCode={countryCode} />
                            <span className={countryNameStyle}>{displayNameOfCountry(countryCode)}</span>
                          </span>
                        </Combobox.Item>
                      )}
                    </Combobox.Collection>
                  </Combobox.Group>
                )}
              </Combobox.List>
            </Combobox.Root>

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ required: { value: true, message: t('validation:required') } }}
      />
    </div>
  );
};
